import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Spinner, Alert } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { getCase } from '../services/api';
import { usePolling } from '../usePolling';
import { POLL_INTERVAL_MS } from '../config';
import { VariantA } from './casedetail/VariantA';

type Case = components['schemas']['Case'];

/**
 * Case detail (hyperlinkable /cases/:caseId). Loads the case + the caller's roles and renders the
 * action-first tabbed layout. Auto-refreshes every 30s while the tab is visible (and immediately
 * on regaining focus) so a user sees others' updates — new messages, uploads, status changes —
 * without a manual reload. Background refreshes are quiet (no loading spinner) and bump `tick`,
 * which the tabbed child panels key on to re-fetch their own data.
 */
export function CaseDetail() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [c, setCase] = useState<Case | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [userId, setUserId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  /** Full load with the loading spinner — first load and after user actions. */
  const load = useCallback(() => {
    if (!caseId) return;
    setLoading(true);
    getCase(caseId)
      .then(setCase)
      .catch(() => setError('Could not load this case (it may not exist or you may not have access).'))
      .finally(() => setLoading(false));
  }, [caseId]);

  /** Quiet background refresh (no spinner) used by polling; bumps tick so children re-fetch. */
  const refreshQuietly = useCallback(() => {
    if (!caseId) return;
    getCase(caseId)
      .then((fresh) => {
        setCase(fresh);
        setTick((t) => t + 1);
      })
      .catch(() => {
        /* transient; keep showing the last good state */
      });
  }, [caseId]);

  useEffect(load, [load]);

  usePolling(refreshQuietly, POLL_INTERVAL_MS);

  useEffect(() => {
    fetchAuthSession()
      .then((s) => {
        const raw = s.tokens?.idToken?.payload?.['cognito:groups'] as string[] | string | undefined;
        const groups = Array.isArray(raw)
          ? raw
          : typeof raw === 'string'
            ? raw.split(',').map((g) => g.trim()).filter(Boolean)
            : [];
        setRoles(groups);
        setUserId((s.tokens?.idToken?.payload?.sub as string) ?? '');
      })
      .catch(() => {
        setRoles([]);
        setUserId('');
      });
  }, []);

  if (loading) {
    return (
      <div className="text-muted d-flex align-items-center">
        <Spinner animation="border" size="sm" className="me-2" /> Loading case…
      </div>
    );
  }
  if (error) {
    return (
      <>
        <Alert variant="danger">{error}</Alert>
        <Link to="/cases">← Back to cases</Link>
      </>
    );
  }
  if (!c) return null;

  return (
    <>
      <Link to="/cases" className="d-inline-block mb-3">← Back to cases</Link>
      <VariantA
        c={c}
        roles={roles}
        currentUserId={userId}
        onChanged={load}
        onReleased={() => navigate('/cases')}
        refreshTick={tick}
      />
    </>
  );
}
